/**
 * Keeps the ServiceNow filter builder aligned with the records it will import.
 *
 * Preview requests are debounced and stale responses are ignored so typing a
 * filter never replaces a newer selection. The backend remains authoritative
 * for the bounded window, display values, and exact pass-through count.
 */
import { useEffect, useState } from 'react'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import { getServiceNowSelectionPreview } from '../services/api'
import type {
  ServiceNowFeedOptions,
  ServiceNowFilter,
  ServiceNowSelectionPreview,
  ServiceNowTable,
} from '../types'

export function useServiceNowSelectionPreview({
  api,
  orgId,
  vaultId,
  open,
  enabled,
  apiUrl,
  table,
  credentialSource,
  recordLimit,
  filters,
}: {
  api: NodeApiClient
  orgId: string
  vaultId: string
  open: boolean
  enabled: boolean
  apiUrl: string
  table: ServiceNowTable
  credentialSource: ServiceNowFeedOptions['credentialSource']
  recordLimit: number
  filters: ServiceNowFilter[]
}) {
  const [preview, setPreview] = useState<ServiceNowSelectionPreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()

  useEffect(() => {
    if (
      !open
      || !enabled
      || !apiUrl
      || !Number.isInteger(recordLimit)
      || recordLimit < 1
      || recordLimit > 500
    ) {
      setPreview(null)
      setLoading(false)
      return
    }
    const completeFilters = filters.filter(filter => filter.value.trim())
    let current = true
    setLoading(true)
    setError(undefined)
    const timer = window.setTimeout(() => {
      void getServiceNowSelectionPreview(api, orgId, vaultId, {
        apiUrl,
        table,
        credentialSource,
        recordLimit,
        filters: completeFilters,
      })
        .then(result => {
          if (current) setPreview(result)
        })
        .catch((requestError: any) => {
          if (current) {
            setError(
              requestError?.response?.data?.detail
              ?? requestError?.message
              ?? 'Live preview is unavailable.',
            )
          }
        })
        .finally(() => {
          if (current) setLoading(false)
        })
    }, 300)
    return () => {
      current = false
      window.clearTimeout(timer)
    }
  }, [
    api,
    apiUrl,
    credentialSource,
    enabled,
    filters,
    open,
    orgId,
    recordLimit,
    table,
    vaultId,
  ])

  return { preview, loading, error }
}
